export const LANG_EN = 'en';
export const LANG_RU = 'ru';

export const MONTH_NAMES_GENITIVE = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
export const MONTH_NAMES_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const LABELS = {
    [LANG_RU]: {
        timeUnknown: 'Время неизвестно',
        today: 'Сегодня',
        tomorrow: 'Завтра',
        at: ' в ',
        ongoingNow: 'Идет сейчас',
        tomorrowAt: 'завтра в ',
        ongoingUntil: 'Идет сейчас, до ',
        monthsShort: MONTH_NAMES_GENITIVE,
        venuePrefix: 'Место: ',
    },
    [LANG_EN]: {
        timeUnknown: 'Time unknown',
        today: 'Today',
        tomorrow: 'Tomorrow',
        at: ' at ',
        ongoingNow: 'Ongoing now',
        tomorrowAt: 'tomorrow at ',
        ongoingUntil: 'Ongoing until ',
        monthsShort: MONTH_NAMES_EN,
        venuePrefix: 'Venue: ',
    },
};

function labels(lang) {
    return LABELS[lang] || LABELS[LANG_RU];
}

export function isTimeUnknown(text) {
    return Object.values(LABELS).some((catalog) => catalog.timeUnknown === text);
}

function dateInTimezoneOffset(utcDate, offsetMinutes) {
    if (utcDate == null || offsetMinutes == null) return utcDate;
    const offsetMs = offsetMinutes * 60 * 1000;
    const utcMs = utcDate.getTime();
    const localMs = utcMs + offsetMs;
    return new Date(localMs);
}

function zonedYmdHm(d, useFixedOffset) {
    if (useFixedOffset) {
        return {
            y: d.getUTCFullYear(),
            m: d.getUTCMonth(),
            d: d.getUTCDate(),
            h: d.getUTCHours(),
            min: d.getUTCMinutes(),
        };
    }
    return {
        y: d.getFullYear(),
        m: d.getMonth(),
        d: d.getDate(),
        h: d.getHours(),
        min: d.getMinutes(),
    };
}

function addOneCalendarDay(dayStart, useFixedOffset) {
    const next = new Date(dayStart.getTime());
    if (useFixedOffset) {
        next.setUTCDate(next.getUTCDate() + 1);
    } else {
        next.setDate(next.getDate() + 1);
    }
    return next;
}

function userLocalDateOnly(instant) {
    return new Date(instant.getFullYear(), instant.getMonth(), instant.getDate());
}

function userTodayAndTomorrow() {
    const now = new Date();
    const today = userLocalDateOnly(now);
    const tomorrow = addOneCalendarDay(today, false);
    return {today, tomorrow};
}

export function formatDateTime(dateString, lang = LANG_RU, timezoneOffsetMinutes = null) {
    if (!dateString) return '';
    const dateUtc = new Date(dateString);
    const L = labels(lang);
    if (isNaN(dateUtc.getTime())) return L.timeUnknown;
    const useFixed = timezoneOffsetMinutes != null;
    const date = useFixed ? dateInTimezoneOffset(dateUtc, timezoneOffsetMinutes) : dateUtc;
    const parts = zonedYmdHm(date, useFixed);
    const {today: userToday, tomorrow: userTomorrow} = userTodayAndTomorrow();
    const eventDayInUserTz = userLocalDateOnly(dateUtc);
    let prefix = '';
    if (eventDayInUserTz.getTime() === userToday.getTime()) prefix = L.today + ', ';
    else if (eventDayInUserTz.getTime() === userTomorrow.getTime()) prefix = L.tomorrow + ', ';
    const months = lang === LANG_EN ? MONTH_NAMES_EN : MONTH_NAMES_GENITIVE;
    const month = months[parts.m];
    const hours = String(parts.h).padStart(2, '0');
    const minutes = String(parts.min).padStart(2, '0');
    return `${prefix}${String(parts.d).padStart(2, '0')} ${month}${L.at}${hours}:${minutes}`;
}

export function getClientTimezoneOffsetMinutes() {
    return -new Date().getTimezoneOffset();
}

export function formatVenueWallClock(dateString, lang, eventOffsetMinutes) {
    if (dateString == null || eventOffsetMinutes == null) return '';
    const dateUtc = new Date(dateString);
    const L = labels(lang);
    if (isNaN(dateUtc.getTime())) return '';
    const date = dateInTimezoneOffset(dateUtc, eventOffsetMinutes);
    const parts = zonedYmdHm(date, true);
    const months = lang === LANG_EN ? MONTH_NAMES_EN : MONTH_NAMES_GENITIVE;
    const month = months[parts.m];
    const hours = String(parts.h).padStart(2, '0');
    const minutes = String(parts.min).padStart(2, '0');
    return `${String(parts.d).padStart(2, '0')} ${month}${L.at}${hours}:${minutes}`;
}

export function formatEventListTimeParts(event, lang = LANG_RU) {
    const L = labels(lang);
    const isOngoing = event && event.is_ongoing;
    const startAt = event && event.start_at;
    const rawOff = event && 'timezone_offset' in event ? event.timezone_offset : null;
    let eventOff = null;
    if (rawOff != null && rawOff !== '') {
        const n = Number(rawOff);
        if (!Number.isNaN(n)) eventOff = n;
    }
    const clientOff = getClientTimezoneOffsetMinutes();
    const showVenue = eventOff !== null && eventOff !== clientOff;
    if (isOngoing) {
        const primary = formatOngoingText(event, lang, null);
        let venue = null;
        if (showVenue) {
            const endAt = event && event.end_at;
            const clock = endAt ? formatVenueWallClock(endAt, lang, eventOff) : '';
            if (clock) venue = `${L.venuePrefix}${clock}`;
        }
        return {primary, venue};
    }
    const primary = formatDateTime(startAt, lang, null);
    let venue = null;
    if (showVenue && startAt) {
        const clock = formatVenueWallClock(startAt, lang, eventOff);
        if (clock) venue = `${L.venuePrefix}${clock}`;
    }
    return {primary, venue};
}

export function formatOngoingText(event, lang = LANG_RU, timezoneOffsetMinutes = null) {
    if (!event) return (LABELS[lang] || LABELS[LANG_RU]).ongoingNow;
    const L = labels(lang);
    const endAt = event && 'end_at' in event ? event['end_at'] : null;
    if (!endAt) return L.ongoingNow;
    const endDateUtc = new Date(endAt);
    if (isNaN(endDateUtc.getTime())) return L.ongoingNow;
    const useFixed = timezoneOffsetMinutes != null;
    const endDate = useFixed ? dateInTimezoneOffset(endDateUtc, timezoneOffsetMinutes) : endDateUtc;
    const endParts = zonedYmdHm(endDate, useFixed);
    const hours = String(endParts.h).padStart(2, '0');
    const minutes = String(endParts.min).padStart(2, '0');
    const {today: userToday, tomorrow: userTomorrow} = userTodayAndTomorrow();
    const endDayInUserTz = userLocalDateOnly(endDateUtc);
    const userYear = new Date().getFullYear();
    const months = lang === LANG_EN ? MONTH_NAMES_EN : MONTH_NAMES_GENITIVE;
    let datePart;
    if (endDayInUserTz.getTime() === userToday.getTime()) datePart = '';
    else if (endDayInUserTz.getTime() === userTomorrow.getTime()) datePart = L.tomorrowAt;
    else {
        const yearPart = endParts.y !== userYear ? ` ${endParts.y}` : '';
        datePart = `${String(endParts.d).padStart(2, '0')} ${months[endParts.m]}${yearPart}${L.at}`;
    }
    return `${L.ongoingUntil}${datePart}${hours}:${minutes}`;
}
